/**
 * The Sync Center's device tools (module 13): offline projects, cached files
 * and a support diagnostics bundle.
 *
 * - **Offline projects**: a project switched off here is no longer pulled
 *   onto this device (its saved copy stays until cleared; queued changes for
 *   it still send). Kept in `kv` (`sync.paused_projects`).
 * - **Cached files** are copies that download again when needed: images the
 *   service worker kept, floor-plan images and drawing files. Clearing them
 *   never touches unsent work (the outbox and photos waiting to upload).
 * - **Diagnostics** describe the device's sync state for support without
 *   personal data: no names, no record contents, no project names, no
 *   messages — counts, states, error codes and timestamps only.
 */
import { db, type OutboxRecord } from './db';
import { getValue, setValue } from './kv';

const PAUSED_KEY = 'sync.paused_projects';
const IMAGE_CACHE = 'kb-images';

export async function pausedProjects(): Promise<string[]> {
  return (await getValue<string[]>(PAUSED_KEY)) ?? [];
}

export async function setProjectOffline(projectId: string, keep: boolean): Promise<string[]> {
  const current = new Set(await pausedProjects());
  if (keep) current.delete(projectId);
  else current.add(projectId);
  const next = [...current].sort();
  await setValue(PAUSED_KEY, next);
  return next;
}

/** Project ids to pull: everything known minus what the person switched off. */
export function withoutPaused(projectIds: readonly string[], paused: readonly string[]): string[] {
  const off = new Set(paused);
  return projectIds.filter((id) => !off.has(id));
}

export type CachedFiles = {
  images: number;
  planImages: number;
  drawingFiles: number;
  bytes: number;
};

export async function cachedFiles(): Promise<CachedFiles> {
  let images = 0;
  try {
    if (typeof caches !== 'undefined') {
      const cache = await caches.open(IMAGE_CACHE);
      images = (await cache.keys()).length;
    }
  } catch {
    images = 0;
  }
  let bytes = 0;
  await db.plan_images.each((row) => {
    bytes += row.blob?.size ?? 0;
  });
  await db.drawing_files.each((row) => {
    bytes += row.blob?.size ?? 0;
  });
  return {
    images,
    planImages: await db.plan_images.count(),
    drawingFiles: await db.drawing_files.count(),
    bytes,
  };
}

/** Drop re-downloadable copies. Never the outbox or the media queue. */
export async function clearCachedFiles(): Promise<void> {
  try {
    if (typeof caches !== 'undefined') await caches.delete(IMAGE_CACHE);
  } catch {
    // The service worker's cache is best effort.
  }
  await db.transaction('rw', db.plan_images, db.drawing_files, async () => {
    await db.plan_images.clear();
    await db.drawing_files.clear();
  });
}

export type LastSync = { project_id: string; updated_at: string };

export async function lastSyncPerProject(): Promise<LastSync[]> {
  const rows = await db.sync_cursors.toArray();
  return rows.map((row) => ({ project_id: row.project_id, updated_at: row.updated_at }));
}

export type DiagnosticsInput = {
  appVersion: string;
  deviceId: string;
  online: boolean;
  counts: Record<string, number>;
  lastSuccessAt: string | null;
  lastErrorCode: string | null;
  storage: { usage: number; quota: number } | null;
  persisted: string;
  userAgent: string;
  language: string;
  now: Date;
};

/** What support needs, and nothing personal (see the module comment). */
export function buildDiagnostics(
  input: DiagnosticsInput,
  outbox: readonly OutboxRecord[],
  cursors: readonly LastSync[],
  tables: Record<string, number>,
  paused: readonly string[],
): Record<string, unknown> {
  return {
    kind: 'koros-build-diagnostics',
    generated_at: input.now.toISOString(),
    app_version: input.appVersion,
    device: input.deviceId.slice(0, 13),
    user_agent: input.userAgent,
    language: input.language,
    online: input.online,
    storage: input.storage,
    storage_persistence: input.persisted,
    sync: {
      counts: input.counts,
      last_success_at: input.lastSuccessAt,
      last_error_code: input.lastErrorCode,
    },
    outbox: outbox.map((record) => ({
      entity_type: record.entity_type,
      operation: record.operation,
      state: record.state,
      attempts: record.attempts,
      created_at_device: record.created_at_device,
      error_code: record.last_error?.code ?? null,
      project: record.project_id ? record.project_id.slice(0, 8) : null,
    })),
    projects: cursors.map((c) => ({
      project: c.project_id.slice(0, 8),
      last_pull_at: c.updated_at,
      offline: !paused.includes(c.project_id),
    })),
    tables,
  };
}

/** Row counts per local table (no contents). */
export async function tableCounts(): Promise<Record<string, number>> {
  const out: Record<string, number> = {};
  for (const table of db.tables) {
    try {
      out[table.name] = await table.count();
    } catch {
      out[table.name] = -1;
    }
  }
  return out;
}
